import { useAnalytics } from '@/contexts/AnalyticsContext';
import { Lead, PartialLead } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/v2/Tabs';
import { collection } from 'firebase/firestore';
import { Import } from 'iconoir-react';
import { Plus } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LeadsTable } from '../components/LeadsTable';
import { PartialLeadsTable } from '../components/PartialLeadsTable';
import { UserContext } from '../contexts/UserContext';
import ExportLeadsPopover from '../popovers/ExportLeadsPopover';
import { emptyConverter } from '../utils';

type LeadViewType = 'leads' | 'partialLeads';

export default function Leads() {
  const { selectedAccount } = useContext(UserContext);
  const { slug } = useParams();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const [viewType, setViewType] = useState<LeadViewType>('leads');

  const leadsQuery = useMemo(() => {
    if (!selectedAccount) return;
    return collection(selectedAccount.ref, 'leads').withConverter(
      emptyConverter<Lead>(),
    );
  }, [selectedAccount]);

  const partialLeadsQuery = useMemo(() => {
    if (!selectedAccount) return;
    return collection(selectedAccount.ref, 'partialLeads').withConverter(
      emptyConverter<PartialLead>(),
    );
  }, [selectedAccount]);

  return (
    <div className="flex h-full flex-col gap-4 px-3 py-3 lg:px-10 lg:py-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="mt-1 text-sm text-gray-600">
            View and manage all your leads collected through your funnels.
          </p>
        </div>
        <div className="flex flex-row gap-2.5">
          <Tabs
            value={viewType}
            onValueChange={(value) => setViewType(value as LeadViewType)}
          >
            <TabsList size="adaptive">
              <TabsTrigger value="leads">Leads</TabsTrigger>
              <TabsTrigger value="partialLeads">Partial Leads</TabsTrigger>
            </TabsList>
          </Tabs>
          <ExportLeadsPopover defaultLeadType={viewType}>
            <Button
              className={'rounded-2lg w-10'}
              variant="outline"
              color="white"
              onClick={() => {
                track('export_leads_clicked', {
                  source: 'leads_page',
                });
              }}
            >
              <Import className="h-5 w-5" />
            </Button>
          </ExportLeadsPopover>
          <Button
            className="rounded-2lg font-inter h-10 shrink-0 gap-2.5 tracking-[0.01em] has-[>svg]:px-4.5"
            onClick={() => navigate(`/workspace/${slug}/settings/integrations`)}
          >
            <Plus className="h-5 w-5" />
            Add Integration
          </Button>
        </div>
      </div>
      <div className="relative flex-1">
        {viewType === 'leads' && leadsQuery && (
          <LeadsTable query={leadsQuery} />
        )}
        {viewType === 'partialLeads' && partialLeadsQuery && (
          <PartialLeadsTable query={partialLeadsQuery} />
        )}
      </div>
    </div>
  );
}
